import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG, AppConfig } from 'src/app.config';
import { SessionService } from '../services/session.service';
import { UserService } from '../services/user.service';
import { ConnectService } from '../services/amazon-connect.service';
import { TrackingApp, TrackingUser } from './tracking.model';

/**
 * The ambient context attached to every tracked event. Resolved fresh at
 * capture time so late-arriving values (Okta login, Amazon Connect contact id)
 * are reflected on subsequent events.
 */
export interface TrackingContext {
    app: TrackingApp;
    env: string;
    user: TrackingUser | null;
    sessionId: string | null;
    contactId: string | null;
}

/**
 * Resolves the ambient context for tracking events (PRODSUPP-145765):
 *   - `app`        -> `policy360-thirdparty` when a TP session is present,
 *                     otherwise `policy360-csr`.
 *   - `contactId`  -> Amazon Connect contact id when running inside Connect,
 *                     otherwise null (the app is "mostly, not always" in Connect).
 *   - `user`       -> Okta subject + email when authenticated.
 *   - `sessionId`  -> TP session id when in the third-party flow.
 *   - `env`        -> deployment environment derived from the configured appURL.
 *
 * Every lookup is null-safe so tracking never throws into app flows.
 */
@Injectable({ providedIn: 'root' })
export class TrackingContextService {
    constructor(
        @Inject(APP_CONFIG) private config: AppConfig,
        private sessionService: SessionService,
        private userService: UserService,
        private connectService: ConnectService
    ) {}

    /** Resolves the full ambient context for an event. */
    resolve(): TrackingContext {
        return {
            app: this.resolveApp(),
            env: this.resolveEnv(),
            user: this.resolveUser(),
            sessionId: this.resolveSessionId(),
            contactId: this.resolveContactId(),
        };
    }

    /**
     * `policy360-thirdparty` when a third-party session identifier is present,
     * otherwise `policy360-csr`.
     */
    resolveApp(): TrackingApp {
        try {
            return this.sessionService.hasSessionId() ? 'policy360-thirdparty' : 'policy360-csr';
        } catch {
            return 'policy360-csr';
        }
    }

    /** TP session id when in the third-party flow, otherwise null. */
    resolveSessionId(): string | null {
        try {
            return this.sessionService.getSessionId();
        } catch {
            return null;
        }
    }

    /**
     * Amazon Connect contact id when the app is running inside Connect,
     * otherwise null. Never throws.
     */
    resolveContactId(): string | null {
        try {
            return this.connectService.contactId ?? null;
        } catch {
            return null;
        }
    }

    /** Okta subject + email when authenticated, otherwise null. */
    resolveUser(): TrackingUser | null {
        try {
            const claims = this.userService.getUser();
            if (!claims || !claims.sub) {
                return null;
            }
            return {
                id: claims.sub ?? null,
                email: (claims.email as string | undefined) ?? null,
            };
        } catch {
            return null;
        }
    }

    /**
     * Derives the environment label from the configured appURL
     * (e.g. `hestia-dev` -> `dev`). Falls back to `prod` when the host carries
     * no recognizable non-prod suffix, and to `unknown` when appURL is absent.
     */
    resolveEnv(): string {
        const appUrl = this.config?.appURL;
        if (!appUrl) {
            return 'unknown';
        }
        try {
            const host = new URL(appUrl).hostname;
            const match = host.match(/hestia-([a-z0-9]+)\./i);
            if (match && match[1]) {
                return match[1].toLowerCase();
            }
            // Non-prod subdomains carry a suffix; the bare prod host does not.
            return this.config.production ? 'prod' : 'unknown';
        } catch {
            return this.config?.production ? 'prod' : 'unknown';
        }
    }
}
